import * as React from "react";
import {connect} from "react-redux";
import {OnFailure, OnSuccess} from "../../../../../store/action/actionHelpers";
import {Dispatch} from "redux";
import Toast from "../../../../helper/Toast";
import Asset from "../../../../../model/asset/Asset";
import {fetchAsset, updateAsset} from "../../../../../store/action/asset/asset";
import AssetFragmentFormView from "./AssetFragmentFormView";
import {Option} from "ts-option";
import AssetMaterialsBreakdown from "../../../../../model/asset/AssetMaterialsBreakdown";
import {RootState} from "../../../../../store/reducer/root/RootState";
import {getAsset} from "../../../../../store/view/asset";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import Loading from "../../../../ui/loading/Loading";

interface IComponentProps {
	/**
	 * On close after cancel or update.
	 */
	onClose: () => void;
	/**
	 * Asset id.
	 */
	assetId: string;
	/**
	 * List of materials for each merchant
	 */
	materialsBreakdown: Option<AssetMaterialsBreakdown[]>;
}

interface IDispatchProps {
	/**
	 * Update asset.
	 * @param {asset} asset
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateAsset: (asset: Asset, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch asset
	 * @param assetId
	 * @param onFailure
	 */
	fetchAsset: (assetId: string, onFailure: OnFailure) => void
}

interface IState {
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
}

interface ReduxProps {
	asset: Option<Asset>,
}

type Props = IComponentProps & IDispatchProps & ReduxProps;

class AssetFragmentFormComponent extends React.PureComponent<Props, IState> {
	constructor(props: Props) {
		super(props);
		this.state = {isSubmitting: false};
		this.onSubmit = this.onSubmit.bind(this);
		this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
		this.onSubmitFailure = this.onSubmitFailure.bind(this);
	}

	componentDidMount() {
		if (this.props.asset.isEmpty) {
			this.props.fetchAsset(this.props.assetId, (e) => {
				notifyBugsnagAndShowToast(e, "Fetch asset");
				this.props.onClose()
			})
		}
	}

	render() {
		return (
			this.props.asset.map(a =>
				<AssetFragmentFormView onSubmit={this.onSubmit}
									   onCancel={this.props.onClose}
									   isSubmitting={this.state.isSubmitting}
									   asset={a}
									   materialsBreakdown={this.props.materialsBreakdown}/>
			).getOrElse(<Loading isLoading={true}/>)
		)
	}

	onSubmit(editedAsset: Asset) {
		this.setState({isSubmitting: true}, () => {
			const assetToSubmit = editedAsset.copy({id: this.props.assetId});
			this.props.updateAsset(
				assetToSubmit,
				this.onSubmitSuccess,
				(error) => {
					let errorText = "Asset update failed";
					if (error.response.data && error.response.data.exception === "AssetCategoryDisabledException") {
						errorText = "Update rejected because asset category is disabled";
					}
					this.onSubmitFailure(errorText)
				}
			);
		});
	}

	onSubmitSuccess() {
		this.setState({isSubmitting: false}, () => {
			Toast.success("Asset updated", "Asset update");
			this.props.onClose();
		});
	}

	onSubmitFailure(text: string) {
		this.setState({isSubmitting: false}, () => Toast.error(text, "Asset update"));
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateAsset: (asset: Asset, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(updateAsset(asset, onSuccess, onFailure)),
		fetchAsset: (assetId: string, onFailure: OnFailure) => dispatch(fetchAsset(assetId, () => {}, onFailure))
	};
}

function mapStateToProps(state: RootState, props: IComponentProps) {
	return {
		asset: getAsset(props.assetId)
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(AssetFragmentFormComponent);